/**
 * App-wide commands: the ⌘K palette, the purchase planner and "sync now".
 *
 * Each can be reached from several places — the sidebar, the dashboard, the
 * mobile bar and the palette itself — so the open/closed state lives here,
 * once, and the dialogs are mounted once. Two copies of the planner open at
 * the same time would be the result otherwise.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { toast } from "sonner";

import { CommandPalette } from "@/components/CommandPalette";
import { PlannerDialog } from "@/components/caps/PlannerDialog";
import { syncApi } from "@/lib/api/client";

interface Commands {
  openPalette: () => void;
  openPlanner: () => void;
  syncNow: () => void;
  /** The same sync, awaited — pull-to-refresh waits on it to stop spinning. */
  syncNowAsync: () => Promise<unknown>;
  syncing: boolean;
}

const CommandContext = React.createContext<Commands | null>(null);

export function CommandProvider({ children }: { children: React.ReactNode }) {
  const [palette, setPalette] = React.useState(false);
  const [planner, setPlanner] = React.useState(false);
  const qc = useQueryClient();

  // Same mutation key as the Settings page's sync button, so the sync pill
  // shows the spinner whichever one started it.
  const sync = useMutation({
    mutationKey: ["sync", "run"],
    mutationFn: () => syncApi.run(false),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["sync"] });
      qc.invalidateQueries({ queryKey: ["statements"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      if (result.ok) toast.success(result.message);
      else toast.error(result.error ?? "Sync failed.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const value = React.useMemo<Commands>(
    () => ({
      openPalette: () => setPalette(true),
      openPlanner: () => {
        setPalette(false);
        setPlanner(true);
      },
      syncNow: () => sync.mutate(),
      syncNowAsync: () => sync.mutateAsync().catch(() => undefined),
      syncing: sync.isPending,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sync.isPending],
  );

  return (
    <CommandContext.Provider value={value}>
      {children}
      <CommandPalette open={palette} onOpenChange={setPalette} />
      <PlannerDialog open={planner} onOpenChange={setPlanner} />
    </CommandContext.Provider>
  );
}

export function useCommands(): Commands {
  const ctx = React.useContext(CommandContext);
  if (!ctx) throw new Error("useCommands must be used inside <CommandProvider>.");
  return ctx;
}

/** "⌘" on Apple platforms, "Ctrl" everywhere else — the key the user presses. */
export function modKey(): string {
  if (typeof navigator === "undefined") return "Ctrl";
  return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? "⌘" : "Ctrl";
}
